import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import TerrainRoundedIcon from '@mui/icons-material/TerrainRounded';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';
import {
  DEFAULT_FIELD_ALLOWANCE_PREFIXES,
  setFieldAllowancePrefixesCache,
} from '../../hooks/useFieldAllowanceDefaultPrefixes';
import {
  DEFAULT_PROJECT_NUMBER_PREFIXES,
  setProjectNumberPrefixesCache,
} from '../../hooks/useProjectNumberPrefixes';

// รหัสนำหน้าเลขโครงการ: ตัวอักษร/ตัวเลขล้วน (ห้ามมี _ เพราะ _ คือตัวคั่นระหว่างรหัสกับปี) — เก็บเป็นตัวพิมพ์ใหญ่
const PREFIX_RE = /^[A-Z0-9]+$/;
const DEFAULT_BYPASS_LABELS = ['JSID_00001', 'JSID_00002', 'JSID_00003'];

const sameList = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// การ์ดตั้งค่า 1 หัวข้อ: หมายเลขขั้น + ไอคอน + คำอธิบาย + สถานะค่าที่ใช้อยู่ + ปุ่มบันทึก/ยกเลิกของตัวเอง
export function SettingSection({
  step, icon: Icon, title, description, status, isCustom, dirty, saving, disabled, onSave, onReset, children,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        borderColor: dirty ? 'primary.main' : 'divider',
        transition: 'border-color .2s',
      }}
    >
      <Stack direction="row" spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={(theme) => ({
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          })}
        >
          <Icon />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              ข้อ {step}
            </Typography>
            <Chip
              size="small"
              label={isCustom ? 'กำหนดเอง' : 'ค่าเริ่มต้น'}
              color={isCustom ? 'primary' : 'default'}
              variant={isCustom ? 'filled' : 'outlined'}
              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
            />
            {dirty && (
              <Chip
                size="small"
                label="ยังไม่ได้บันทึก"
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
              />
            )}
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
            {description}
          </Typography>

          <Box sx={{ mt: 2.5 }}>{children}</Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {status}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={(theme) => ({
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, 0.025),
        })}
      >
        <Button size="small" color="inherit" onClick={onReset} disabled={!dirty || saving}>
          ยกเลิกการแก้ไข
        </Button>
        <Button size="small" variant="contained" onClick={onSave} disabled={!dirty || saving || disabled}>
          บันทึก
        </Button>
      </Stack>
    </Paper>
  );
}

// หน้า "รหัสโครงการ" (/management/project-prefixes) — ตั้งค่า 3 อย่างที่ผูกกับรหัสนำหน้าเลขโครงการ
// ทั้งหมดเก็บใน Information (project_number_prefixes / field_allowance_default_prefixes / prepare_bypass_projects)
// แต่ละหัวข้อบันทึกเฉพาะ field ของตัวเอง
export default function ProjectPrefixSettings({ title }) {
  const [infoId, setInfoId] = useState(null);
  // รายการที่เก็บใน DB จริง (ว่างได้ — ว่างแล้วระบบใช้ค่าเริ่มต้นแทนเอง ไม่ถูกเขียนลง DB)
  const [prefixes, setPrefixes] = useState([]);
  const [saved, setSaved] = useState([]);
  const [faPrefixes, setFaPrefixes] = useState([]);
  const [savedFaPrefixes, setSavedFaPrefixes] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [bypassIds, setBypassIds] = useState([]);
  const [savedBypassIds, setSavedBypassIds] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/information/`);
        setInfoId(res.data?.id || res.data?._id || null);
        const asList = (v) => (Array.isArray(v) ? v : []);
        const list = asList(res.data?.project_number_prefixes);
        const fa = asList(res.data?.field_allowance_default_prefixes);
        const ids = asList(res.data?.prepare_bypass_projects).map(String);
        setPrefixes(list);
        setSaved(list);
        setFaPrefixes(fa);
        setSavedFaPrefixes(fa);
        setBypassIds(ids);
        setSavedBypassIds(ids);
      } catch (e) {
        console.error('project prefixes load error', e);
      }
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/project?size=5000&page=1`);
        setProjectOptions((res.data?.rows || []).map((p) => ({
          id: String(p.id || p._id),
          label: `${p.project_number || ''} ${p.name || ''}`.trim(),
        })));
      } catch (e) {
        console.error('project list load error', e);
      }
    })();
  }, []);

  const handleAdd = () => {
    const value = input.trim().toUpperCase();
    if (!value) return;
    if (!PREFIX_RE.test(value)) {
      setError('ใช้ได้เฉพาะตัวอักษรอังกฤษและตัวเลข (ห้ามเว้นวรรคหรือ _)');
      return;
    }
    if (prefixes.includes(value)) {
      setError('มีรหัสนี้อยู่แล้ว');
      return;
    }
    setPrefixes((prev) => [...prev, value]);
    setInput('');
    setError('');
  };

  const handleDelete = (value) => {
    setPrefixes((prev) => prev.filter((p) => p !== value));
    // รหัสที่ถูกลบออกจากรายการ ต้องหลุดจากรายการรหัสค่าออกสนามที่ยังไม่บันทึกด้วย
    setFaPrefixes((prev) => prev.filter((p) => p !== value));
    setError('');
  };

  const toggleFaPrefix = (value) => {
    setFaPrefixes((prev) => (prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]));
  };

  const dirty = !sameList(prefixes, saved);
  const faDirty = !sameList(faPrefixes, savedFaPrefixes);
  const bypassDirty = !sameList(bypassIds, savedBypassIds);

  // รหัสที่ระบบใช้จริงตอนนี้ (ว่าง = ค่าเริ่มต้น)
  const effectivePrefixes = prefixes.length ? prefixes : DEFAULT_PROJECT_NUMBER_PREFIXES;
  const faOptions = effectivePrefixes;

  const yy = useMemo(() => String(new Date().getFullYear() % 100).padStart(2, '0'), []);

  const save = async (key, body, after) => {
    if (!infoId) return;
    setSavingKey(key);
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/information/${infoId}`, body);
      after();
      showAlert('บันทึกแล้ว');
    } catch (e) {
      console.error('project prefixes save error', e);
      showAlert('บันทึกไม่สำเร็จ');
    } finally {
      setSavingKey('');
    }
  };

  const savePrefixes = () => save('prefix', { project_number_prefixes: prefixes }, () => {
    setSaved(prefixes);
    setProjectNumberPrefixesCache(effectivePrefixes);
  });
  const saveFa = () => save('fa', { field_allowance_default_prefixes: faPrefixes }, () => {
    setSavedFaPrefixes(faPrefixes);
    setFieldAllowancePrefixesCache(faPrefixes.length ? faPrefixes : DEFAULT_FIELD_ALLOWANCE_PREFIXES);
  });
  const saveBypass = () => save('bypass', { prepare_bypass_projects: bypassIds }, () => {
    setSavedBypassIds(bypassIds);
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ตั้งค่ารหัสนำหน้าเลขโครงการ และกติกาที่ผูกกับรหัสหรือโครงการ แต่ละข้อบันทึกแยกกัน
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <Stack spacing={2.5}>
        <SettingSection
          step={1}
          icon={TagRoundedIcon}
          title="รหัสนำหน้าเลขโครงการ"
          description={`รหัสที่เลือกได้ตอนสร้างโครงการ เลขโครงการจะเป็น รหัส_ปี(2 หลัก)เลข(3 หลัก) เช่น ${effectivePrefixes[0]}_${yy}001 การเพิ่มหรือลบรหัสไม่กระทบเลขโครงการที่สร้างไปแล้ว`}
          status={prefixes.length
            ? `ใช้รหัส ${prefixes.join(' / ')}`
            : `ยังไม่ได้ตั้งค่า — ระบบใช้ ${DEFAULT_PROJECT_NUMBER_PREFIXES.join(' / ')} เป็นค่าเริ่มต้น`}
          isCustom={prefixes.length > 0}
          dirty={dirty}
          saving={savingKey === 'prefix'}
          disabled={!infoId}
          onSave={savePrefixes}
          onReset={() => { setPrefixes(saved); setFaPrefixes(savedFaPrefixes); setError(''); }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, minHeight: 36 }}>
            {effectivePrefixes.map((p) => (
              <Chip
                key={p}
                label={`${p}_${yy}001`}
                onDelete={prefixes.length ? () => handleDelete(p) : undefined}
                variant={prefixes.length ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem', height: 34 }}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            placeholder="เพิ่มรหัสใหม่ เช่น JS"
            value={input}
            onChange={(e) => {
              setInput(e.target.value.toUpperCase());
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            error={Boolean(error)}
            helperText={error || 'ตัวอักษรอังกฤษ/ตัวเลขเท่านั้น กด Enter เพื่อเพิ่ม'}
            sx={{ width: { xs: '100%', sm: 320 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleAdd} disabled={!input.trim()} startIcon={<AddRoundedIcon />}>
                    เพิ่ม
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </SettingSection>
      </Stack>

        <Stack spacing={2.5}>
        <SettingSection
          step={2}
          icon={TerrainRoundedIcon}
          title="รหัสโครงการที่ได้ค่าออกสนามเป็นค่าเริ่มต้น"
          description="วันที่พนักงานลงเวลาในโครงการที่เลขขึ้นต้นด้วยรหัสที่เลือก ระบบถือว่าได้ค่าออกสนามให้ก่อน (ถ้า HR ยังไม่ได้ติ๊กเอง) ใช้กับรายงานเงินเดือนที่คำนวณแบบเดิม"
          status={faPrefixes.length
            ? `ได้ค่าออกสนามเป็นค่าเริ่มต้น: ${faPrefixes.join(' / ')}`
            : `ยังไม่ได้เลือก — ระบบใช้ ${DEFAULT_FIELD_ALLOWANCE_PREFIXES.join(' / ')} เป็นค่าเริ่มต้น`}
          isCustom={faPrefixes.length > 0}
          dirty={faDirty}
          saving={savingKey === 'fa'}
          disabled={!infoId}
          onSave={saveFa}
          onReset={() => setFaPrefixes(savedFaPrefixes)}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            กดเลือกรหัสที่ต้องการ (เลือกได้หลายรหัส)
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {faOptions.map((p) => {
              const on = faPrefixes.includes(p);
              return (
                <Chip
                  key={p}
                  label={p}
                  clickable
                  onClick={() => toggleFaPrefix(p)}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  icon={on ? <CheckRoundedIcon /> : undefined}
                  sx={{ fontWeight: 700, height: 34 }}
                />
              );
            })}
          </Stack>
        </SettingSection>

        <SettingSection
          step={3}
          icon={BoltRoundedIcon}
          title="โครงการที่เบิกแล้วเตรียมจ่ายได้ทันที"
          description="รายการเบิกของโครงการเหล่านี้ที่อนุมัติและตรวจสอบครบแล้ว เข้าหน้าเตรียมจ่ายได้เลยโดยไม่ต้องรอข้ามวัน"
          status={bypassIds.length
            ? `ใช้เฉพาะ ${bypassIds.length} โครงการที่เลือก (ไม่ใช้ ${DEFAULT_BYPASS_LABELS.join(' / ')} อีก)`
            : `ยังไม่ได้เลือก — ระบบใช้ ${DEFAULT_BYPASS_LABELS.join(' / ')} เป็นค่าเริ่มต้น`}
          isCustom={bypassIds.length > 0}
          dirty={bypassDirty}
          saving={savingKey === 'bypass'}
          disabled={!infoId}
          onSave={saveBypass}
          onReset={() => setBypassIds(savedBypassIds)}
        >
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={projectOptions}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={bypassIds.map((id) => projectOptions.find((o) => o.id === id) || { id, label: id })}
            onChange={(e, list) => setBypassIds(list.map((o) => o.id))}
            renderInput={(params) => (
              <TextField {...params} placeholder={bypassIds.length ? '' : 'ค้นหาและเลือกโครงการ'} />
            )}
          />
          {bypassIds.length === 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              {DEFAULT_BYPASS_LABELS.map((n) => (
                <Chip key={n} size="small" variant="outlined" label={n} sx={{ fontFamily: 'monospace' }} />
              ))}
            </Stack>
          )}
        </SettingSection>
        </Stack>
      </Box>
    </Box>
  );
}
